import { ReactElement } from 'react';

import { Placement, ToastLevel, ToastType } from '@philips-emr/tws-constants';

import { CloseEvent } from '../component/types';

export interface ToastProps {
  /**
   * TBD
   */
  action?: ReactElement;
  /**
   * Whether the toast will be able to be dismissed through a close icon.
   */
  dismissible: boolean;
  /**
   * Expression to use when rendering the Dismiss link.
   */
  dismissExpression?: string;
  /**
   * Indicates that the toast should be highlighted. Will follow the `contentTonalRange` of the application.
   */
  highlight?: boolean;
  /**
   * The severity of the toast.
   */
  level?: ToastLevel;
  /**
   * The messsage to be shown
   */
  message: string;
  /**
   * The offset added to the base location of the toast.
   */
  offset?: number;
  /**
   * A function that will be called before the toast is closed.
   */
  onBeforeClose?: (event: CloseEvent) => void;
  /**
   * A function that will be called when the toast is closed.
   */
  onClose?: (event: CloseEvent) => void;
  /**
   * The placement of the toast.
   */
  placement?: Placement.BOTTOM | Placement.TOP;
  /**
   * TBD
   */
  showIcon?: boolean;
  /**
   * The timeout used to hide the toast automatically.
   */
  timeout?: number;
  /**
   * The title of the toast
   */
  title?: string;
  /**
   * The type of the Toast
   */
  type?: ToastType;
}

type Listener = (toast: ToastProps) => void;

interface ToastService {
  /**
   * This method adds a new listener that will be called everytime that there's an intent to open new toast.
   *
   * @example
   * // Will log all of the toast properties.
   * ToastService.addListener((toast: ToastProps) => {
   *  console.log('toast', toast);
   * });
   *
   * @param fn - The listener to be added.
   */
  addListener: (fn: Listener) => () => void;
  /**
   * This method is used to manifestate the intent to open a new toast, which will then be rendered by components that implement `addListener` method.
   *
   * @example
   * ToastService.open({
   *  dismissible: true,
   *  message: 'This is my message!',
   *  title: 'My custom title!'
   * });
   *
   * @param toast - An object containing the toast properties.
   */
  open: (toast: ToastProps) => void;
}

function createToastService(): ToastService {
  const listeners: Set<Listener> = new Set();

  const addListener = (fn: Listener): () => void => {
    if (listeners.has(fn)) {
      throw Error('[Toast Service]: This listener is already listening to new toasts');
    }

    listeners.add(fn);

    return (): void => {
      listeners.delete(fn);
    };
  };

  const open = (toast: ToastProps): void => {
    listeners.forEach((listener) => {
      listener(toast);
    });
  };

  return {
    addListener,
    open,
  };
}

const toastService = createToastService();

export default toastService;
